import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const computeRange = (mode, anchor) => {
  const d = new Date(anchor + "T00:00:00");
  if (mode === "dia") return { start: anchor, end: anchor };
  if (mode === "semana") {
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: iso(monday), end: iso(sunday) };
  }
  if (mode === "mes") {
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: iso(first), end: iso(last) };
  }
  return { start: null, end: null };
};

const fmtPT = (s) => new Date(s + "T00:00:00").toLocaleDateString("pt-PT", { day: "numeric", month: "short", year: "numeric" });

const rangeLabel = (mode, range) => {
  if (mode === "todas") return "Todos os registos";
  if (mode === "dia") return fmtPT(range.start);
  if (mode === "semana") return `${fmtPT(range.start)} — ${fmtPT(range.end)}`;
  return new Date(range.start + "T00:00:00").toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
};

export const PeriodPicker = ({ onRange, allowAll = false, defaultMode = "mes", testPrefix = "period" }) => {
  const [mode, setMode] = useState(defaultMode);
  const [anchor, setAnchor] = useState(iso(new Date()));

  const range = useMemo(() => computeRange(mode, anchor), [mode, anchor]);

  useEffect(() => {
    onRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  const shift = (dir) => {
    const d = new Date(anchor + "T00:00:00");
    if (mode === "dia") d.setDate(d.getDate() + dir);
    else if (mode === "semana") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(iso(d));
  };

  const modes = [...(allowAll ? [["todas", "Todas"]] : []), ["dia", "Dia"], ["semana", "Semana"], ["mes", "Mês"]];

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex border border-black/15">
        {modes.map(([id, label]) => (
          <button
            key={id}
            data-testid={`${testPrefix}-mode-${id}`}
            onClick={() => setMode(id)}
            className={`label-editorial px-5 py-3 transition-colors duration-200 ${
              mode === id ? "bg-black text-white" : "bg-white text-black/60 hover:text-black"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {mode !== "todas" && (
        <div className="flex items-center gap-1 bg-white border border-black/15">
          <button data-testid={`${testPrefix}-prev`} onClick={() => shift(-1)} className="p-3 hover:bg-black/5" aria-label="Anterior">
            <ChevronLeft size={16} />
          </button>
          <input
            data-testid={`${testPrefix}-anchor-input`}
            type="date"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
            className="px-2 py-3 text-sm font-light focus:outline-none"
          />
          <button data-testid={`${testPrefix}-next`} onClick={() => shift(1)} className="p-3 hover:bg-black/5" aria-label="Seguinte">
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      <p data-testid={`${testPrefix}-label`} className="label-editorial text-black/50">
        {rangeLabel(mode, range)}
      </p>
    </div>
  );
};
